import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';

import { Nullable } from '@common/typescript/objects/Nullable';

import { List } from '@common/typescript/objects/List';

import { ApplicationState } from '@app/store';
import { Location, LocationInfo, locationName } from '@app/objects/Location';
import { bindItemsActionCreators } from '@app/store/ItemList';

import { Select } from '@app/components/UI/Select/Select';
import { Option } from '@app/components/UI/Select/Option';
import { formatAddress } from '@app/utilities/address';

import { useRequest } from '@app/hooks/useRequest';

type Selector<T> = (items: Array<Location>) => T;

interface LocationSelectProps {
	value?: Nullable<number>;
	onChange: (value: Nullable<number>, item: Nullable<Location>) => void;

	clinicId?: number;
	local?: boolean;
	forMyClinic?: boolean;

	disabled?: boolean | Selector<boolean>;
	preselect?: number | Selector<Nullable<number> | undefined>;

	placeholder?: string;
	title?: string;

	text?: (item: Location) => React.ReactNode;
	label?: (item: Location) => React.ReactNode;
}

function getLocation(id: Nullable<number>, list: Array<Location>): Nullable<Location> {
	if (id === null) return null;

	return list.find((item: Location) => item.id === id) ?? null;
}

interface LocationListRequestParams {
	objectId: number;
	count: number;
	offset: number;
}
function loadLocations(
	params: LocationListRequestParams,
	onReload: (data?: LocationListRequestParams | undefined) => Promise<List<LocationInfo> | void>,
) {
	return onReload({
		objectId: params.objectId,
		count: params.count,
		offset: params.offset,
	});
}

export const LocationSelect: React.FC<LocationSelectProps> = (props: LocationSelectProps) => {
	const reduxSlice = useSelector((state: ApplicationState) => state.clinicLocations);

	const dispatch = useDispatch();
	const factory = React.useMemo(() => bindItemsActionCreators<Location>(dispatch), [dispatch]);

	const localListRequest = useRequest<List<Location>, LocationListRequestParams>('locationList', undefined, { requestOnMount: false });
	const onLoadLocalLocations = React.useCallback((params: LocationListRequestParams) => loadLocations(params, localListRequest.reload), [localListRequest.reload]);

	React.useEffect(() => {
		if (!props.local) return;

		factory.reqPages('clinicLocations', 'locationList', 'location', {
			objectId: props.clinicId,
			getMine: props.forMyClinic,
			count: 20,
			offset: 0,
		});
	}, [factory, props.clinicId, props.forMyClinic]);

	React.useEffect(() => {
		if (props.local) return;
		if (!props.clinicId) return;

		onLoadLocalLocations({
			objectId: props.clinicId,
			count: 20,
			offset: 0,
		});
	}, [props.clinicId]);

	const locations: Array<Location> = React.useMemo(() => {
		if (!props.local) return localListRequest.item?.list ?? [];

		return reduxSlice?.items ?? [];
	}, [props.clinicId, localListRequest.item, reduxSlice]);

	const loading = !props.local
		? localListRequest.loading
		: !!reduxSlice?.isLoading;

	React.useEffect(() => {
		if (props.preselect === undefined) return;
		const preselect = (typeof props.preselect === 'function' ? props.preselect(locations) : props.preselect) ?? null;

		if (preselect !== (props.value ?? null)) {
			const item = getLocation(preselect, reduxSlice.items);
			props.onChange(preselect, item);
		}
	}, [props.preselect, locations]);

	const label = props.label!;
	const text = props.text!;

	return (
		<Select
			value={props.value}
			onChange={(value: number) => props.onChange(value, getLocation(value, locations))}

			disabled={typeof props.disabled === 'function' ? props.disabled(locations) : props.disabled}
			title={props.title}
			placeholder={props.placeholder}

			showSearch
			showClear
			loading={loading}

			optionFilterProp="children"
			optionLabelProp="label"
		>
			<Option key={-1} value={-1} disabled>{props.placeholder}</Option>
			{
				locations.map((item: Location) => (
					<Option
						key={item.id}
						value={item.id}
						label={text(item)}
					>
						{label(item)}
					</Option>
				))
			}
		</Select>
	);
};

LocationSelect.defaultProps = {
	local: true,
	forMyClinic: false,
	placeholder: 'Select Clinic Location',
	title: 'Clinic Location',
	text: locationName,
	label: (location: Location) => (
		<Space
			direction="vertical"
			size={0}
		>
			<Typography.Text>{location.name}</Typography.Text>
			<Typography.Text style={{ opacity: 0.6, fontSize: '.9em', lineHeight: 1.1 }}>{formatAddress(location)}</Typography.Text>
		</Space>
	),
	disabled: false,
};
